import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import { WebhookEvents, WEBHOOK_EVENT_DESCRIPTIONS, WEBHOOK_EVENT_DISPLAY_NAMES, emptyWebhookEvents } from './webhookEvents';

interface WebhookConfig {
    id?: string;
    name: string;
    target_url: string;
    auth_secret?: string;
    has_auth_secret?: boolean;
    is_active: boolean;
    events: WebhookEvents;
}

interface WebhookModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    webhook: WebhookConfig | null;
    onSave: (webhook: Partial<WebhookConfig>) => Promise<any>;
    saving: boolean;
}

const WebhookModal: React.FC<WebhookModalProps> = ({
    open,
    onOpenChange,
    webhook,
    onSave,
    saving
}) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<WebhookConfig>({
        name: '',
        target_url: '',
        auth_secret: '',
        is_active: true,
        events: emptyWebhookEvents()
    });

    const [errors, setErrors] = useState<{ name?: string; target_url?: string }>({});
    const [authSecretTouched, setAuthSecretTouched] = useState(false);

    useEffect(() => {
        if (webhook) {
            setFormData({
                ...webhook,
                auth_secret: undefined,
            });
        } else {
            setFormData({
                name: '',
                target_url: '',
                auth_secret: '',
                is_active: true,
                events: emptyWebhookEvents()
            });
        }
        setAuthSecretTouched(false);
        setErrors({});
    }, [webhook, open]);

    const handleInputChange = (field: keyof WebhookConfig, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleEventToggle = (event: keyof WebhookConfig['events']) => {
        setFormData(prev => ({
            ...prev,
            events: {
                ...prev.events,
                [event]: !prev.events[event]
            }
        }));
    };

    const handleAuthSecretChange = (value: string) => {
        setAuthSecretTouched(true);
        handleInputChange('auth_secret', value);
    };

    const handleClearAuthSecret = () => {
        setAuthSecretTouched(true);
        handleInputChange('auth_secret', '');
    };

    const validateForm = () => {
        const newErrors: typeof errors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Webhook name is required';
        }
        
        if (!formData.target_url.trim()) {
            newErrors.target_url = 'Webhook URL is required';
        } else if (!isValidUrl(formData.target_url)) {
            newErrors.target_url = 'Please enter a valid URL';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (string: string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        // Check if at least one event is selected
        const hasSelectedEvents = Object.values(formData.events).some(v => v);
        if (!hasSelectedEvents) {
            toast.error('Please select at least one event to subscribe to');
            return;
        }

        const payload = { ...formData };
        if (webhook && !authSecretTouched) {
            delete payload.auth_secret;
        }

        const savedWebhook = await onSave(payload);
        
        // Navigate to webhook details page after successful save (only for new webhooks)
        if (!webhook && savedWebhook && savedWebhook.id) {
            navigate(`/integrations/webhooks/${savedWebhook.id}`);
        }
    };

    const eventDescriptions = WEBHOOK_EVENT_DESCRIPTIONS;

    const eventDisplayNames = WEBHOOK_EVENT_DISPLAY_NAMES;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{webhook ? 'Edit Webhook' : 'Create New Webhook'}</DialogTitle>
                    <DialogDescription>
                        Configure a webhook endpoint to receive real-time event notifications
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Webhook Name</Label>
                        <Input
                            id="name"
                            placeholder="e.g., Production CRM Webhook"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className={errors.name ? 'border-red-500' : ''}
                        />
                        {errors.name && (
                            <p className="text-sm text-red-500">{errors.name}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="target_url">Webhook URL</Label>
                        <Input
                            id="target_url"
                            type="url"
                            placeholder="https://api.example.com/webhooks/aimdoc"
                            value={formData.target_url}
                            onChange={(e) => handleInputChange('target_url', e.target.value)}
                            className={errors.target_url ? 'border-red-500' : ''}
                        />
                        {errors.target_url && (
                            <p className="text-sm text-red-500">{errors.target_url}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="auth_secret">Bearer Token Secret (Optional)</Label>
                        <Input
                            id="auth_secret"
                            type="password"
                            placeholder={webhook?.has_auth_secret ? "Token is configured. Enter a new value to replace it." : "whsec_customer_generated_secret"}
                            value={formData.auth_secret ?? ''}
                            onChange={(e) => handleAuthSecretChange(e.target.value)}
                        />
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-xs text-muted-foreground">
                                When configured, Aimdoc sends this as <span className="font-mono">Authorization: Bearer &lt;token&gt;</span>.
                                {webhook?.has_auth_secret && !authSecretTouched ? ' Leave blank to keep the existing token.' : ''}
                            </p>
                            {webhook?.has_auth_secret && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleClearAuthSecret}
                                    disabled={saving}
                                >
                                    Remove Token
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch
                            id="is_active"
                            checked={formData.is_active}
                            onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                        />
                        <Label htmlFor="is_active">Active</Label>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                        <Label>Events</Label>
                        <p className="text-sm text-muted-foreground">
                            Select which events should trigger this webhook
                        </p>
                        <div className="grid grid-cols-2 gap-4 mt-3">
                            {Object.entries(eventDescriptions).map(([event, description]) => (
                                <div key={event} className="flex items-start space-x-2">
                                    <Checkbox
                                        id={event}
                                        checked={formData.events[event as keyof typeof formData.events]}
                                        onCheckedChange={() => handleEventToggle(event as keyof typeof formData.events)}
                                        className="mt-0.5"
                                    />
                                    <div className="space-y-0.5">
                                        <Label htmlFor={event} className="font-medium cursor-pointer text-sm">
                                            {eventDisplayNames[event] || event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            {description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            webhook ? 'Update Webhook' : 'Create Webhook'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WebhookModal;
